import { yupResolver } from "@hookform/resolvers/yup";
import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";
import {
  createSeries,
  distributionDetailsSchema,
} from "../../../../utils/mainTabs/distribute/createSeries";
import classes from "./DistributionDetails.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { setErrorHandler } from "../../../../store/actions/actionHandlers/errorActionHandler";
import { errorTypes } from "../../../../utils/errorTypes";
import Input from "../../../../components/Common/Input/Input";
import Button from "../../../../components/Common/Button/Button";
import InfoIcon from "../../../../components/Common/SvgIcons/SocialIcons/InfoIcon";
import { getSearchClassTagsActionHandler } from "../../../../store/actions/actionHandlers/manageActionHandler";
import SelectThumbnail from "../../../../containers/manage/CreateShotClass/SelectThumbnail/SelectThumbnail";
import { isCollectionNameExistsActionHandler } from "../../../../store/actions/actionHandlers/distributeActionHandler";
import { mode } from "../../../../utils/formModes";
import { FormattedMessage } from "react-intl";

const DistributionDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [validTagSearch, setValidTagSearch] = useState(true);
  const [searchTagsList, setSearchTagsList] = useState([]);
  const { formValues, setFormValues, tenantName } = useOutletContext();
  const { formFields } = createSeries.distributionDetails;
  const [fields, setFields] = useState([]);
  const [imageLibrary, setImageLibrary] = useState(0);
  const [isNameExistLoader, setIsNameExistLoader] = useState(false);
  const [duplicateNameErrorMsg, setDuplicateNameErrorMsg] = useState("");
  const [seriesName, setSeriesName] = useState("");
  const methods = useForm({
    defaultValues: {
      ...formValues,
    },
    resolver: yupResolver(distributionDetailsSchema),
  });
  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;
  const ImageUrl = watch("ImageUrl");
  const canDisplayClassPostDuedate = watch("CanDisplayClassPostDuedate");
  const distributeState = useSelector((state) => state.distributeReducer);
  const { classAndCollectionDataByProgramId } = distributeState;

  const fileChange = useCallback(
    (event) => {
      event.preventDefault();
      let files = event.target.files[0];
      const accept = ["image/png", "image/jpeg", "image/gif", "image/bmp"];
      if (!accept.includes(files.type)) {
        setValue("ImageUrl", "");
        dispatch(
          setErrorHandler(
            errorTypes.WARN,
            "Please select a valid format from [.png, .jpg, .gif, .bmp]"
          )
        );
      }
    },
    [setValue, dispatch]
  );

  const handleSearchTags = useCallback(
    (e) => {
      let val = e.target.value.trim() !== "" && e.target.value;
      if (val) {
        if (/^[A-Za-z0-9@# ]+$/.test(val)) {
          setValidTagSearch(true);
          dispatch(
            getSearchClassTagsActionHandler(e.target.value, setSearchTagsList)
          );
        } else {
          setValidTagSearch(false);
        }
      } else {
        setValidTagSearch(true);
        setSearchTagsList([]);
      }
    },
    [dispatch]
  );

  const seriesNameChanage = useCallback(
    (e) => {
      const val = e?.target?.value?.trim();
      setSeriesName(val);
      if (val) {
        dispatch(
          isCollectionNameExistsActionHandler(
            val,
            setIsNameExistLoader,
            setDuplicateNameErrorMsg,
            "Program"
          )
        );
      }
    },
    [dispatch]
  );

  const handleClickFileButton = () => {
    setImageLibrary(3);
  };

  useEffect(() => {
    setFields(
      formFields(
        fileChange,
        handleSearchTags,
        validTagSearch,
        searchTagsList,
        handleClickFileButton,
        seriesNameChanage,
        isNameExistLoader,
        location.pathname ===
          `${createSeries.copyPath(tenantName)}/${
            params?.programId
          }/seriesDetails`
          ? mode.COPY
          : location.pathname ===
            `${createSeries.editPath(tenantName)}/${
              params?.programId
            }/seriesDetails`
          ? mode.EDIT
          : mode.ADD
      )
    );
  }, [
    formFields,
    fileChange,
    handleSearchTags,
    validTagSearch,
    searchTagsList,
    seriesNameChanage,
    isNameExistLoader,
    location,
    params,
    tenantName,
  ]);

  useEffect(() => {
    if (classAndCollectionDataByProgramId?.length > 0) {
      const programData = classAndCollectionDataByProgramId?.[0].Programs;
      setValue(
        "Name",
        location.pathname ===
          `${createSeries.copyPath(tenantName)}/${
            params?.programId
          }/notificationSettings`
          ? "copy"
          : "" + programData?.Name
      );
      setValue(
        "Tags",
        programData?.Tags?.split(",")?.map((val) => ({
          TagName: val,
        }))
      );
      setValue(
        "CanDisplayClassPostDuedate",
        programData?.CanDisplayClassPostDuedate
      );
      setValue("DueDate", programData?.DueDate?.split("T")?.[0]);
      setValue("IsSelfPacedProgram", programData?.IsSelfPacedProgram);
      setValue("IsSpreadProgram", programData?.IsSpreadProgram);
      setValue("ImageUrl", programData?.ImageUrl);
    }
  }, [
    classAndCollectionDataByProgramId,
    setValue,
    location,
    params,
    tenantName,
  ]);

  const onSubmit = (data) => {
    if (!duplicateNameErrorMsg) {
      setFormValues({ ...formValues, ...data });
      if (
        location.pathname ===
        `${createSeries.copyPath(tenantName)}/${
          params?.programId
        }/seriesDetails`
      )
        navigate(
          `${createSeries.copyPath(tenantName)}/${
            params?.programId
          }/notificationSettings`
        );
      else if (
        location.pathname ===
        `${createSeries.editPath(tenantName)}/${
          params?.programId
        }/seriesDetails`
      )
        navigate(
          `${createSeries.editPath(tenantName)}/${
            params?.programId
          }/notificationSettings`
        );
      else navigate(createSeries.notificationSettings.path(tenantName));
    }
  };

  const handleCancelFileInput = () => {
    setValue("ImageUrl", null);
  };

  const resetForm = () => {
    reset({
      CanDisplayClassPostDuedate: false,
      DueDate: "",
      ImageUrl: null,
      IsSelfPacedProgram: false,
      IsSpreadProgram: false,
      Name: "",
      Tags: [],
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        className={classes.distribution_details}
        onSubmit={handleSubmit(onSubmit)}
      >
        {imageLibrary === 3 ? (
          <SelectThumbnail setActiveSection={setImageLibrary} name="ImageUrl" />
        ) : (
          <>
            <div className={classes.form}>
              {fields.map((item) => (
                <div className={classes.form_item} key={item.key}>
                  <Input
                    key={item.key}
                    label={item.label}
                    name={item.name}
                    type={item.type}
                    labelColor={item.labelColor}
                    width={item.width}
                    required={item.required}
                    min={item.min}
                    max={item.max}
                    error={
                      item?.name === "Name" && seriesName
                        ? duplicateNameErrorMsg
                        : item?.name === "DueDate" && canDisplayClassPostDuedate
                        ? ""
                        : item?.name === "ImageUrl" && ImageUrl
                        ? ""
                        : errors?.[item?.name]?.message
                    }
                    hidden={item.hidden}
                    direction={item.direction}
                    placeholder={item.placeholder}
                    accept={item.accept}
                    onChange={item.onChange}
                    valueArr={item?.valueArr}
                    isValidTagSearch={item.validTagSearch}
                    searchTagsList={item.searchTagsList}
                    fieldName={item.fieldName}
                    fieldValue={item.fieldValue}
                    disabled={
                      item?.name === "DueDate" && canDisplayClassPostDuedate
                        ? true
                        : item?.disable
                    }
                    handleClickFileButton={item?.handleClickFileButton}
                    fileInputName={ImageUrl?.FileName}
                    handleCancelFileInput={handleCancelFileInput}
                    loading={item?.loading}
                  />
                  {item?.info && (
                    <div className={classes.icon}>
                      {/* <Tooltip direction="bottom" content={item?.info}> */}
                      <InfoIcon />
                      {/* </Tooltip> */}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className={classes.footer}>
              <div className={classes.clear_all} onClick={resetForm}>
                {" "}
                <FormattedMessage id="CLEAR_ALL_TXT" />
              </div>
              <Button label={"Save & Next"} small dark type="submit" />
            </div>
          </>
        )}
      </form>
    </FormProvider>
  );
};

export default DistributionDetails;
